/* Shared pure rules: usable in browser and node tests, no runtime dependencies. */
(function (root) {
  const allowed = [1, 2, 3, 5, 10, 25, 50];
  function packSize(title) {
    // Optional "/ Finish" suffix: Shopify titles become "10 Cards / Matte" once a Finish option exists.
    const match = String(title).trim().match(/^(1|2|3|5|10|25|50)\s+Cards?(?:\s*\/.*)?$/i);
    return match ? Number(match[1]) : null;
  }
  // The card counter moves through the pack sizes only (1, 2, 3, 5, 10, 25, 50), never to a multiple of a pack.
  // Returns the next pack above (direction > 0) or below the count, or null past either end.
  function stepPack(count, direction) {
    const n = Number(count) || 0;
    const next = direction > 0 ? allowed.find(size => size > n) : allowed.slice().reverse().find(size => size < n);
    return next === undefined ? null : next;
  }
  function validProperties(input) {
    const common = ['Card purpose','Card base','Front logo','Back','Orientation','Finish','Business name','Business location','Website','Social handle'];
    const byPurpose = {Reviews:['Review platform'],Social:['Social platform'],Booking:['Booking status'],Contact:['Contact name','Contact title','Contact phone','Contact email','Contact website']};
    const keys = common.concat(byPurpose[input['Card purpose']] || []);
    return Object.fromEntries(keys.filter(key => String(input[key] || '').trim()).map(key => [key, String(input[key]).trim()]));
  }
  // The review sites a Reviews card's link must be on, by its "Review platform" (another platform takes any web link).
  // Google's own business-profile links count: a review page (g.page/r/…), a Maps or Search share link.
  const REVIEW_HOSTS = { Google: ['google.com', 'g.page', 'maps.app.goo.gl', 'goo.gl', 'g.co', 'share.google'], Yelp: ['yelp.com'], Trustpilot: ['trustpilot.com'] };
  // A whole web link: http(s) (or no scheme at all), a dotted public host, no spaces, no user or port; on one of
  // `hosts` when given. String rules only, so snippets/pocket-design-gap.liquid can say exactly the same.
  function webLink(value, hosts) {
    const raw = String(value ?? '').trim(), lower = raw.toLowerCase();
    if (!raw || raw.length > 2048 || /[ \n]/.test(raw)) return false;
    let rest = raw;
    if (lower.startsWith('https://')) rest = raw.slice(8);
    else if (lower.startsWith('http://')) rest = raw.slice(7);
    // Another scheme (mailto:, tel:) or a port without a scheme.
    else if (raw.includes(':') && !/[/?#]/.test(raw.split(':')[0])) return false;
    const host = rest.split(/[/?#]/)[0].toLowerCase();
    if (!host || /[@:]/.test(host) || !host.includes('.') || /^[\d.]+$/.test(host) || host.startsWith('.') || host.endsWith('.')) return false;
    if (/(^|\.)(localhost|local|internal|test|invalid)$/.test(host)) return false;
    return !hosts || hosts.some(domain => host === domain || host.endsWith('.' + domain));
  }
  // The link in what was pasted (Oct 22): a share sheet copies "Triple R Towing https://maps.app.goo.gl/…", or the name,
  // address and link on their own lines (a one-line field drops the line breaks, so the link can follow a word with no
  // space), and every link check refuses that. The first https:// or http:// address in it that is a whole web link
  // (wrapping quotes, brackets and an ending period dropped); otherwise the text as it is.
  function linkIn(text) {
    const raw = String(text ?? '').trim(), bare = word => word.replace(/^[("'<‘“]+|[)"'>’”.,;!?]+$/g, '');
    if (!/\s/.test(raw) && !/.https?:\/\//i.test(raw)) return webLink(raw) || !webLink(bare(raw)) ? raw : bare(raw);
    for (const [found] of raw.matchAll(/https?:\/\/\S+/gi)) if (webLink(bare(found))) return bare(found);
    return raw;
  }
  const PURPOSES = ['Website', 'Reviews', 'Booking', 'Social', 'Contact', 'Other'];
  // Where each card type keeps the link a tap opens (Destination is the one the store writes; the others are its fields).
  const LINK_FIELDS = { Reviews: ['Review link', 'Destination', 'Review destination'], Website: ['Destination', 'Website destination', 'Website'], Booking: ['Destination', 'Booking destination'], Social: ['Destination', 'Social destination'], Other: ['Destination', 'Other destination'] };
  /**
   * No design, no order (Oct 22): what a card line still needs before it can check out, '' when nothing.
   * 'design': this storefront's design (_Design pf2-…) with its artwork saved (_Artwork state "ready" and a saved logo,
   * name image, full-card art or prepared artwork), or a front that is the business name alone. 'type': its card type.
   * 'options': finish, card color, front, back and orientation. 'link': a web link a tap opens (a Reviews card's on its
   * review site; a platform's name is not a link). 'contact': a Contact card's first name and a phone number, email or
   * website, or a contact link. Every buy path on the theme asks this (assets/pocket.js: the bar, Buy now, quick pay, the
   * form, the cart drawer); sections/pocket-cart.liquid asks snippets/pocket-design-gap.liquid, the same rule.
   */
  function designGap(properties) {
    const p = properties || {}, has = key => String(p[key] ?? '').trim() !== '';
    const prepared = String(p['_Prepared artwork'] ?? '').trim();
    const artwork = ['_Logo mark asset', '_Wordmark asset', '_Background art'].some(has) || (prepared !== '' && prepared !== '{}');
    const nameOnly = p['_Artwork source'] === 'name-only' && (has('Wordmark') || has('Business name'));
    if (!/^pf2-/.test(String(p._Design || '')) || p['_Artwork state'] !== 'ready' || (!artwork && !nameOnly)) return 'design';
    const purpose = String(p['Card purpose'] || '');
    if (!PURPOSES.includes(purpose)) return 'type';
    if (!['Matte', 'Glossy'].includes(p.Finish) || !['Card base', 'Front logo', 'Back', 'Orientation'].every(has)) return 'options';
    if (purpose === 'Contact') return webLink(p['Contact destination']) || (has('Contact first') && ['Contact phone number', 'Contact email', 'Contact website'].some(has)) ? '' : 'contact';
    const hosts = purpose === 'Reviews' ? REVIEW_HOSTS[p['Review platform']] : null;
    return LINK_FIELDS[purpose].some(key => webLink(p[key], hosts)) ? '' : 'link';
  }
  const designComplete = properties => !designGap(properties);
  // Native Shopify checkout, kept locale-aware by building on the storefront root URL.
  function checkoutPath(rootUrl) {
    return String(rootUrl || '/').replace(/\/?$/, '/') + 'checkout';
  }
  // Quantity artwork exists for 1/3/5/10/25/50. The 2-card pack uses the closest visual: 3 cards.
  function quantityMediaPack(title) {
    const size = packSize(title);
    if (size === null) return null;
    return size === 2 ? 3 : size;
  }
  // The one place the theme names its Worker. A ?pf_worker= override is honoured only on local or preview hosts and only
  // for local or staging Workers, so a link on the live store can never point the storefront at another host.
  const PRODUCTION_WORKER = 'https://pocketfactory-brand.bryton-p-zoz.workers.dev';
  const STAGING_WORKER = 'https://pocketfactory-brand-staging.bryton-p-zoz.workers.dev';
  const WORKER_KEY = 'pf_worker';
  function allowedWorker(value) {
    try {
      const url = new URL(String(value));
      if (url.username || url.password) return null;
      if (url.origin === STAGING_WORKER) return url.origin;
      if (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) return url.origin;
    } catch (_) {}
    return null;
  }
  function previewHost(location, shopify) {
    const host = String(location?.hostname || '');
    if (host === 'localhost' || host === '127.0.0.1' || /\.shopifypreview\.com$/i.test(host)) return true;
    try { if (new URLSearchParams(location?.search || '').has('preview_theme_id')) return true; } catch (_) {}
    const role = shopify?.theme?.role;
    return Boolean(role) && role !== 'main';
  }
  function workerBase(env) {
    const location = env?.location || root.location;
    if (!previewHost(location, env ? env.Shopify : root.Shopify)) return PRODUCTION_WORKER;
    let store = env?.storage;
    try { if (store === undefined) store = root.sessionStorage; } catch (_) { store = null; }
    try {
      const asked = new URLSearchParams(location?.search || '').get(WORKER_KEY);
      if (asked !== null) {
        const origin = allowedWorker(asked);
        if (origin) store?.setItem(WORKER_KEY, origin); else store?.removeItem(WORKER_KEY);
      }
      return allowedWorker(store?.getItem(WORKER_KEY)) || PRODUCTION_WORKER;
    } catch (_) {
      return PRODUCTION_WORKER;
    }
  }
  // One id per browser tab (sessionStorage "pf_sid"), sent as X-PF-Session to the help chat and the AI design
  // routes so the Worker can hold each visit to its caps (3 AI designs, 20 help messages). Random, never personal.
  const SESSION_KEY = 'pf_sid';
  const SESSION_ID = /^[A-Za-z0-9_-]{16,64}$/;
  let sessionCache = '';
  function sessionId(env) {
    if (!env && sessionCache) return sessionCache;
    let store = env?.storage;
    try { if (store === undefined) store = root.sessionStorage; } catch (_) { store = null; }
    try { const have = store?.getItem(SESSION_KEY); if (have && SESSION_ID.test(have)) return env ? have : (sessionCache = have); } catch (_) {}
    const bytes = new Uint8Array(16);
    (env?.crypto || root.crypto || globalThis.crypto).getRandomValues(bytes);
    const id = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    try { store?.setItem(SESSION_KEY, id); } catch (_) {}
    if (!env) sessionCache = id;
    return id;
  }
  // The Worker's public switches (GET /v1/capabilities: cardProfiles, aiArt, helpChat, ...), fetched once per page and
  // shared by the help chat and "Generate your card design". Resolves {} when the Worker cannot be reached.
  let capabilitiesPromise = null;
  function capabilities() {
    if (capabilitiesPromise) return capabilitiesPromise;
    const fetcher = root.fetch;
    if (typeof fetcher !== 'function') return Promise.resolve({});
    const controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    const timer = controller ? root.setTimeout(() => controller.abort(), 4000) : 0;
    capabilitiesPromise = fetcher.call(root, workerBase() + '/v1/capabilities', { headers: { Accept: 'application/json' }, credentials: 'omit', signal: controller?.signal })
      .then(response => (response.ok ? response.json() : {}))
      .then(data => (data && typeof data === 'object' ? data : {}))
      .catch(() => ({}))
      .finally(() => { if (timer) root.clearTimeout(timer); });
    return capabilitiesPromise;
  }
  const api = { allowed, packSize, stepPack, validProperties, checkoutPath, quantityMediaPack, workerBase, allowedWorker, sessionId, capabilities, PRODUCTION_WORKER, REVIEW_HOSTS, webLink, linkIn, designGap, designComplete };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.PocketCore = api;
})(typeof window !== 'undefined' ? window : globalThis);
